'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { c } from '../../../utils/tailwind-merge';
import { FormModal } from '../../molecules/FormModal';
import { WriteReviewTips } from '../../molecules/WriteReviewTips';
import {
  MAX_TITLE_CHARS,
  ReviewFormData,
  WriteReviewModalProps,
} from './WriteReviewModal.types';

import Image from 'next/image';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { interpolateStringWithData } from 'utils';
import { InputV2 } from '../../atoms/Input.v2';
import { Textarea } from '../../atoms/Textarea';
import { StarsV2, StarsV2Props } from '../../molecules/Stars.v2';
import { MAX_FEEDBACK_CHARS } from '../FeedbackModal';

export const WriteReviewModal = ({
  microcopy,
  onSubmitReview,
  onEmailPublisher,
  title,
  data = { rating: 0, title: '', review: '' },
  version,
  ownedDate,
  imageUrl,
  reviewModalTrigger,
  className = '',
}: WriteReviewModalProps) => {
  const reviewFormSchema = z.object({
    rating: z.number().min(1, { message: microcopy.ratingValidationMessage }),
    title: z.string().min(1, { message: microcopy.titleValidationMessage }),
    review: z.string().min(1, { message: microcopy.reviewValidationMessage }),
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    watch,
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewFormSchema),
    mode: 'onChange',
    defaultValues: data,
  });

  const onFormSubmit = (data: ReviewFormData) => {
    onSubmitReview(data, true);
  };

  const watchedReview = watch('review');
  const watchedTitle = watch('title');

  const renderSectionHeader = (title: string) => (
    <h3 className="body mb-2 w-full font-medium tracking-tighter text-grey-900">
      {title}
    </h3>
  );

  const starProps = (
    field: ControllerRenderProps<ReviewFormData, 'rating'>
  ): StarsV2Props => ({
    editable: true,
    variant: 'blue',
    ariaLabel: microcopy.rateExperience,
    value: field.value,
    onChange: (value: number) => field.onChange(value),
    max: 5,
  });

  const modalContent = (
    <div className="flex flex-col py-4">
      <figure className="mb-5 flex [&_p]:mb-0 md:[&_p]:mb-1">
        <Image
          src={imageUrl}
          alt={title}
          aria-hidden="true"
          className="mr-5 h-20 w-20 rounded-lg"
          width={80}
          height={80}
        />
        <figcaption>
          <p className="md:body text-sm font-medium">{title}</p>
          <p className="text-sm text-grey-600">
            {interpolateStringWithData(microcopy.version, { version })}
          </p>
          <p className="text-sm text-grey-600">
            {interpolateStringWithData(microcopy.ownedDate, {
              date: ownedDate,
            })}
          </p>
        </figcaption>
      </figure>
      <div className="mb-2 flex flex-col md:mb-2 md:flex-row">
        <div className="mr-0 md:mr-8 md:w-2/3">
          {renderSectionHeader(microcopy.rateExperience)}
          <Controller
            control={control}
            name="rating"
            render={({ field }) => (
              <>
                <StarsV2
                  {...starProps(field)}
                  height={24}
                  width={24}
                  className="mb-5 hidden items-start md:block"
                />
                <StarsV2
                  {...starProps(field)}
                  height={48}
                  width={48}
                  className="mb-5 block items-start md:hidden"
                />
              </>
            )}
          />
          {renderSectionHeader(microcopy.titleReview)}
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <>
                <InputV2
                  id="review-title"
                  data-testid="review-title"
                  placeholder={microcopy.titleReviewPlaceholder}
                  {...field}
                  maxLength={MAX_TITLE_CHARS}
                  aria-invalid={Boolean(errors.title)}
                  className="h-10 w-full flex-row"
                />
                <p className="mb-5 ml-3 text-xs text-grey-500">
                  {interpolateStringWithData(microcopy.remainingTitle, {
                    characters: Math.max(
                      0,
                      MAX_TITLE_CHARS - watchedTitle.length
                    ).toString(),
                  })}
                </p>
              </>
            )}
          />
          {renderSectionHeader(microcopy.writeReview)}
          <Controller
            control={control}
            name="review"
            render={({ field }) => (
              <>
                <Textarea
                  id="write-review-textarea"
                  data-testid="write-review-textarea"
                  placeholder={microcopy.writeReviewPlaceholder}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  maxLength={MAX_FEEDBACK_CHARS}
                  className="h-44 min-h-0 w-full flex-1 flex-row md:h-3/5"
                  aria-invalid={Boolean(errors.review)}
                  name={field.name}
                />
                <p className="mb-0 ml-3 text-xs text-grey-500">
                  {interpolateStringWithData(microcopy.remaining, {
                    characters: Math.max(
                      0,
                      MAX_FEEDBACK_CHARS - watchedReview.length
                    ).toString(),
                  })}
                </p>
              </>
            )}
          />
        </div>
        <div className="mt-6 md:mt-0 md:w-1/3">
          <WriteReviewTips
            {...microcopy.tips}
            onEmailPublisher={onEmailPublisher}
          />
        </div>
      </div>
    </div>
  );

  return (
    <FormModal
      trigger={reviewModalTrigger}
      content={modalContent}
      title={microcopy.title}
      close={microcopy.close}
      submit={microcopy.submit}
      disabled={!isValid}
      cancel={microcopy.cancel}
      onSubmit={handleSubmit(onFormSubmit)}
      className={c('bg-white', className)}
      size="large"
    />
  );
};
